import {
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Paper,
  Popper,
  TextField,
  Typography,
  styled,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Address, AddressRequest, ResponseObject } from "../../classes/main";
import { ApiService } from "@/app/services/AppService";
import { getToken } from "../../services/HolderService";
import { useLoadScript } from "@react-google-maps/api";
import type { Libraries } from "@googlemaps/js-api-loader";

interface AutocompletePrediction {
  description: string;
  place_id: string;
  structured_formatting: {
    main_text: string;
    secondary_text: string;
  };
  terms: Array<{
    offset: number;
    value: string;
  }>;
  types: string[];
}

const API_KEY = "AIzaSyCY519Z3pdrcYUmVhOV8NftANVlYYoVRKQ";

const Input = styled(TextField)(({ theme }) => ({
  "& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button": {
    display: "none",
  },
  "& input[type=number]": {
    MozAppearance: "textfield",
  },
}));

interface AddressInputDialogProps {
  addressOpen: any;
  handleClose: any;
  addressForSave: any;
  updateAddressList: any;
  updateAddressFlag: any;
}

export const AddressInputDialog: React.FC<AddressInputDialogProps> = ({
  addressOpen,
  handleClose,
  addressForSave,
  updateAddressList,
  updateAddressFlag,
}) => {
  const [address, setAddress] = React.useState<Address | undefined>(undefined);
  const [addressLimitFlag, setAddressLimitFlag] = useState(false);
  const [addressLimitMessage, setAddressLimitMessage] = useState<
    string | undefined
  >(undefined);
  const [clicked, setClicked] = React.useState(false);
  const [predictions, setPredictions] = useState<AutocompletePrediction[]>([]);
  const [inputValue, setInputValue] = useState("");
  const apiService: ApiService = useMemo(() => new ApiService(), []);
  const libraries = useRef<Libraries>(["places"]);
  const autocompleteService =
    useRef<google.maps.places.AutocompleteService | null>(null);

  const { isLoaded } = useLoadScript({
    googleMapsApiKey: API_KEY,
    libraries: libraries.current,
  });

  useEffect(() => {
    if (isLoaded && window.google) {
      autocompleteService.current =
        new google.maps.places.AutocompleteService();
    }
  }, [isLoaded]);

  React.useEffect(() => {
    if (addressForSave) {
      setAddress({
        building: addressForSave.building || "",
        locality: addressForSave.locality || "",
        landmark: addressForSave.formattedAddress || "",
        placeId: addressForSave.placeId || "",
        latitude: addressForSave.latitude || "",
        longitude: addressForSave.longitude || "",
      });
    }
  }, [addressForSave]);

  const handleAddress = (e: any) => {
    setAddress((prevState) => ({
      ...prevState,
      [e.target.name]: e.target.value,
    }));
  };
  const handleSubmit = (e: any) => {
    if (
      address?.building == null ||
      address?.building == undefined ||
      address?.building.trim() == ""
    ) {
      setClicked(true);
      return;
    } else if (
      address?.locality == null ||
      address?.locality == undefined ||
      address?.locality.trim() == ""
    ) {
      setClicked(true);
      return;
    } else if (
      address?.landmark == null ||
      address?.landmark == undefined ||
      address?.landmark.trim() == ""
    ) {
      setClicked(true);
      return;
    }

    const addressRequest: AddressRequest = new AddressRequest();
    addressRequest.building = address.building;
    addressRequest.locality = address.locality;
    addressRequest.formattedAddress = address.landmark;
    addressRequest.placeId = address.placeId;
    addressRequest.latitude = address.latitude;
    addressRequest.longitude = address.longitude;
    apiService
      .post(apiService.saveAddress, addressRequest, getToken()!)
      .then((value: unknown) => {
        const responseObject = value as ResponseObject; // Type assertion
        if (responseObject?.status == "success") {
          const savedAddress: Address = responseObject?.object as Address;
          localStorage.setItem("address", btoa(JSON.stringify(savedAddress)));
          handleClose();
          updateAddressList();
        } else if (responseObject?.status == "failed") {
          setAddressLimitFlag(true);
          setAddressLimitMessage(responseObject?.message);
        }
      });
  };

  const handleUpdateAddress = (e: any) => {
    if (
      address?.building == null ||
      address?.building == undefined ||
      address?.building.trim() == ""
    ) {
      setClicked(true);
      return;
    } else if (
      address?.locality == null ||
      address?.locality == undefined ||
      address?.locality.trim() == ""
    ) {
      setClicked(true);
      return;
    }

    const addressRequest: AddressRequest = new AddressRequest();

    addressRequest.building = address.building;
    addressRequest.locality = address.locality;
    addressRequest.formattedAddress = address.landmark;
    addressRequest.placeId = addressForSave.placeId;
    addressRequest.latitude = addressForSave.latitude;
    addressRequest.longitude = addressForSave.longitude;
    apiService
      .put(apiService.updateCustomerAddresses, addressRequest, getToken()!)
      .then((value: unknown) => {
        const responseObject = value as ResponseObject; // Type assertion
        if (responseObject?.status == "success") {
          handleClose();
          updateAddressList();
        }
      });
  };

  const getPlacePredictionsAsync = async (input: string) => {
    return new Promise<google.maps.places.AutocompletePrediction[]>(
      (resolve, reject) => {
        autocompleteService.current?.getPlacePredictions(
          { input, componentRestrictions: { country: ["IN", "NP"] } },
          (predictions, status) => {
            if (status === google.maps.places.PlacesServiceStatus.OK) {
              resolve(predictions || []);
            } else {
              reject(new Error("Failed to fetch predictions"));
            }
          }
        );
      }
    );
  };

  const handleInputChange = async (
    event: React.ChangeEvent<{}>,
    newInputValue: string
  ) => {
    setInputValue(newInputValue);
    try {
      if (autocompleteService.current && newInputValue) {
        const predictions = await getPlacePredictionsAsync(newInputValue);
        setPredictions(predictions);
      }
    } catch (error) {
      console.error(error);
      setPredictions([]);
    }
  };

  const handlePlaceChange = (event: React.ChangeEvent<{}>, newValue: any) => {
    if (!newValue || !newValue?.place_id) return;

    const placesService = new google.maps.places.PlacesService(
      document.createElement("div")
    );

    placesService?.getDetails(
      { placeId: newValue?.place_id },
      (placeDetails: google.maps.places.PlaceResult | null) => {
        if (placeDetails) {
          const address: Address = new Address();
          address.placeId = newValue?.place_id;
          address.formattedAddress = placeDetails?.formatted_address;
          address.savedAddress = false;

          address.latitude = placeDetails?.geometry?.location?.lat();
          address.longitude = placeDetails?.geometry?.location?.lng();
          setAddress((prevState: any) => ({
            ...prevState,
            landmark: placeDetails?.formatted_address,
            placeId: newValue?.place_id,
            latitude: placeDetails?.geometry?.location?.lat(),
            longitude: placeDetails?.geometry?.location?.lng(),
          }));
        }
      }
    );
  };

  const CustomPaper = styled(Paper)(({ theme }) => ({
    "&::-webkit-scrollbar": {
      display: "none",
    },
    msOverflowStyle: "none",
    scrollbarWidth: "none",
    width: "-webkit-fill-available",
    marginTop: "1rem",
  }));

  const CustomPopper = (props: any) => {
    return (
      <Popper
        {...props}
        style={{ ...props.style, width: "20rem" }}
        placement="bottom-start"
      >
        {props.children}
      </Popper>
    );
  };

  return (
    <Dialog
      open={addressOpen}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      sx={{
        "@media (max-width: 600px)": {
          "& .MuiDialog-paper": {
            width: "100%",
            height: "100%",
            maxHeight: "100%",
            margin: 0,
          },
        },
      }}
    >
      <Box sx={{ width: "100%", height: "100%" }}>
        <DialogTitle sx={{ textAlign: "right", padding: "1rem" }}>
          <CloseIcon onClick={handleClose} sx={{ cursor: "pointer" }} />
        </DialogTitle>
        <DialogContent>
          <Input
            autoFocus
            required
            margin="dense"
            type="text"
            label="Flat Number/ House Number"
            value={address?.building}
            name="building"
            onChange={handleAddress}
            fullWidth
            sx={{
              "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline":
                {
                  borderColor: "#005200",
                },
              padding: "0px",
            }}
          />
          {address?.building == "" && clicked ? (
            <label style={{ color: "red", fontSize: "14px" }}>
              This filed is required
            </label>
          ) : (
            <div></div>
          )}
          <Input
            autoFocus
            required
            margin="dense"
            type="text"
            label="Locality"
            value={address?.locality}
            name="locality"
            onChange={handleAddress}
            fullWidth
            sx={{
              "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline":
                {
                  borderColor: "#005200",
                },
              padding: "0px",
            }}
          />
          {address?.locality == "" && clicked ? (
            <label style={{ color: "red", fontSize: "14px" }}>
              This field is required
            </label>
          ) : (
            <div></div>
          )}
          {isLoaded && (
            <Autocomplete
              style={{ margin: "8px auto 4px" }}
              ListboxProps={{
                style: { maxHeight: 250, overflow: "auto" }, // Set max height here
              }}
              freeSolo
              inputValue={inputValue}
              options={predictions}
              onInputChange={handleInputChange}
              getOptionLabel={(option: any) => option.description ?? ""}
              onChange={handlePlaceChange} // Using the corrected handler
              PaperComponent={(props) => <CustomPaper {...props} />}
              PopperComponent={CustomPopper}
              renderOption={(props: any, option: any) => {
                const { key, ...restProps } = props;
                return (
                  <Box
                    key={option?.place_id}
                    component="li"
                    {...restProps}
                    sx={{
                      flexDirection: "column",
                      width: "100%",
                      float: "left",
                      marginBottom: "0.5rem",
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        textAlign: "left",
                        width: "100%",
                      }}
                    >
                      {option?.description ?? ""}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        textAlign: "left",
                        width: "100%",
                        fontSize: "11px",
                      }}
                    >
                      {option?.structured_formatting?.main_text ?? ""}
                    </Typography>
                  </Box>
                );
              }}
              sx={{
                width: "100%",
                "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline":
                  {
                    borderColor: "#005200",
                  },
              }}
              renderInput={(params) => (
                <TextField {...params} label="Search Places" />
              )}
            />
          )}
          {address?.landmark == "" && clicked ? (
            <label style={{ color: "red", fontSize: "14px" }}>
              This filed is required
            </label>
          ) : (
            <div></div>
          )}
          {updateAddressFlag ? (
            <Button
              variant="contained"
              color="success"
              fullWidth
              sx={{
                marginTop: "20px",
                padding: "8px 10px",
                borderRadius: "20px",
              }}
              onClick={handleUpdateAddress}
            >
              Update Address
            </Button>
          ) : (
            <Button
              variant="contained"
              color="success"
              fullWidth
              sx={{
                marginTop: "20px",
                padding: "8px 10px",
                borderRadius: "20px",
              }}
              onClick={handleSubmit}
            >
              Save Address
            </Button>
          )}
          {addressLimitFlag ? (
            <Typography
              sx={{
                color: "red",
                margin: "1rem auto 2px",
                textAlign: "center",
              }}
            >
              {addressLimitMessage}
            </Typography>
          ) : (
            ""
          )}
        </DialogContent>
      </Box>
    </Dialog>
  );
};
